import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Snackbar,
  Alert,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  MenuItem,
  Grid,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import TimerIcon from '@mui/icons-material/Timer';
import config from '../../../config';

// HR Overtime ledger — manual entries per employee/date with hours × rate.
// OT auto-computation from attendance + shift math will plug in during
// payroll (Phase 5).
export default function Overtime() {
  const token = sessionStorage.getItem('token');
  const company = JSON.parse(sessionStorage.getItem('companyDetails') || '{}');
  const user = JSON.parse(sessionStorage.getItem('user') || '{}');
  const companyId = company?.id;

  const [employees, setEmployees] = useState([]);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const today = new Date();
  const monthStart = new Date(today.getFullYear(), today.getMonth(), 1)
    .toISOString()
    .slice(0, 10);
  const [empFilter, setEmpFilter] = useState('');
  const [fromDate, setFromDate] = useState(monthStart);
  const [toDate, setToDate] = useState(today.toISOString().slice(0, 10));

  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState({
    employeeId: '',
    otDate: today.toISOString().slice(0, 10),
    otHours: '',
    otRate: '',
    remarks: '',
  });
  const [saving, setSaving] = useState(false);

  const [snack, setSnack] = useState({ open: false, msg: '', sev: 'success' });
  const toast = (m, s = 'success') => setSnack({ open: true, msg: m, sev: s });

  const loadEmployees = async () => {
    const res = await fetch(
      `${config.API_BASE_URL}/Hr/Employees/${companyId}`,
      { headers: { Authorization: `Bearer ${token}` } },
    );
    if (res.ok) setEmployees(await res.json());
  };

  const load = async () => {
    if (!companyId) return;
    setLoading(true);
    try {
      const qs = new URLSearchParams();
      if (empFilter) qs.append('employeeId', empFilter);
      if (fromDate) qs.append('from', fromDate);
      if (toDate) qs.append('to', toDate);
      const res = await fetch(
        `${config.API_BASE_URL}/Hr/Overtime/${companyId}?${qs.toString()}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (!res.ok) throw new Error();
      setRows(await res.json());
    } catch (e) {
      toast('Failed to load overtime', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (companyId) loadEmployees();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId]);

  useEffect(() => {
    const t = setTimeout(() => load(), 200);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId, empFilter, fromDate, toDate]);

  const totals = useMemo(() => {
    const hrs = rows.reduce((a, r) => a + Number(r.otHours || 0), 0);
    const amt = rows.reduce((a, r) => a + Number(r.otAmount || 0), 0);
    return { hrs: Math.round(hrs * 100) / 100, amt };
  }, [rows]);

  const openAdd = () => {
    setForm({
      employeeId: '',
      otDate: today.toISOString().slice(0, 10),
      otHours: '',
      otRate: '',
      remarks: '',
    });
    setDialogOpen(true);
  };

  const save = async () => {
    if (!form.employeeId || !form.otHours || !form.otRate) {
      toast('Employee, hours, rate required', 'warning');
      return;
    }
    setSaving(true);
    try {
      const body = {
        Id: null,
        CompanyId: companyId,
        EmployeeId: Number(form.employeeId),
        OtDate: form.otDate,
        OtHours: Number(form.otHours),
        OtRate: Number(form.otRate),
        Remarks: form.remarks || null,
        UserId: user?.id || null,
      };
      const res = await fetch(`${config.API_BASE_URL}/Hr/Overtime`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (!res.ok || !data.success) {
        toast('Save failed', 'error');
        return;
      }
      toast('Overtime saved');
      setDialogOpen(false);
      load();
    } catch (e) {
      toast('Network error', 'error');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (r) => {
    if (
      !window.confirm(
        `Delete OT entry for ${r.employeeName} on ${new Date(r.otDate).toLocaleDateString('en-IN')}?`,
      )
    )
      return;
    const res = await fetch(
      `${config.API_BASE_URL}/Hr/Overtime/${r.id}/${companyId}`,
      { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } },
    );
    if (res.ok) {
      toast('Deleted');
      load();
    } else toast('Delete failed', 'error');
  };

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: '#F1F5F9', minHeight: '100%' }}>
      <Stack direction='row' spacing={1.5} alignItems='center' sx={{ mb: 2, justifyContent: 'space-between' }}>
        <Stack direction='row' spacing={1.5} alignItems='center'>
          <Box
            sx={{
              width: 42,
              height: 42,
              borderRadius: 2,
              bgcolor: '#f97316',
              color: '#fff',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}>
            <TimerIcon />
          </Box>
          <Box>
            <Typography sx={{ fontWeight: 800, fontSize: '1.1rem' }}>
              Overtime
            </Typography>
            <Typography sx={{ fontSize: '0.75rem', color: '#64748B' }}>
              HR · {rows.length} entry · {totals.hrs} hr · ₹
              {totals.amt.toLocaleString('en-IN')}
            </Typography>
          </Box>
        </Stack>
        <Button
          variant='contained'
          startIcon={<AddIcon />}
          onClick={openAdd}
          sx={{ bgcolor: '#f97316', '&:hover': { bgcolor: '#ea580c' } }}>
          Add Overtime
        </Button>
      </Stack>

      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={1.5}
        sx={{ mb: 2 }}>
        <TextField
          select
          size='small'
          label='Employee'
          value={empFilter}
          onChange={(e) => setEmpFilter(e.target.value)}
          sx={{ bgcolor: '#fff', minWidth: 220 }}>
          <MenuItem value=''>All</MenuItem>
          {employees.map((e) => (
            <MenuItem key={e.id} value={e.id}>
              {e.employeeName} ({e.employeeCode})
            </MenuItem>
          ))}
        </TextField>
        <TextField
          type='date'
          size='small'
          label='From'
          InputLabelProps={{ shrink: true }}
          value={fromDate}
          onChange={(e) => setFromDate(e.target.value)}
          sx={{ bgcolor: '#fff' }}
        />
        <TextField
          type='date'
          size='small'
          label='To'
          InputLabelProps={{ shrink: true }}
          value={toDate}
          onChange={(e) => setToDate(e.target.value)}
          sx={{ bgcolor: '#fff' }}
        />
      </Stack>

      <Card sx={{ borderRadius: 2 }}>
        <CardContent sx={{ p: 0, '&:last-child': { pb: 0 } }}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
              <CircularProgress size={28} />
            </Box>
          ) : (
            <TableContainer>
              <Table size='small'>
                <TableHead>
                  <TableRow sx={{ bgcolor: '#F8FAFC' }}>
                    <TableCell sx={{ fontWeight: 700 }}>Date</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Employee</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Department</TableCell>
                    <TableCell sx={{ fontWeight: 700 }} align='right'>
                      Hours
                    </TableCell>
                    <TableCell sx={{ fontWeight: 700 }} align='right'>
                      Rate
                    </TableCell>
                    <TableCell sx={{ fontWeight: 700 }} align='right'>
                      Amount
                    </TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Source</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Remarks</TableCell>
                    <TableCell sx={{ fontWeight: 700 }} align='right'>
                      Actions
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={9} align='center' sx={{ py: 4 }}>
                        <Typography sx={{ color: '#94a3b8' }}>
                          No overtime entries in this range.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                  {rows.map((r) => (
                    <TableRow key={r.id} hover>
                      <TableCell>
                        {new Date(r.otDate).toLocaleDateString('en-IN')}
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>
                        {r.employeeName}
                        <Typography sx={{ fontSize: '0.65rem', color: '#94a3b8' }}>
                          {r.employeeCode}
                        </Typography>
                      </TableCell>
                      <TableCell>{r.departmentName || '—'}</TableCell>
                      <TableCell align='right'>{r.otHours}</TableCell>
                      <TableCell align='right'>₹{r.otRate}</TableCell>
                      <TableCell align='right' sx={{ fontWeight: 700 }}>
                        ₹{Number(r.otAmount || 0).toLocaleString('en-IN')}
                      </TableCell>
                      <TableCell>{r.source}</TableCell>
                      <TableCell sx={{ color: '#475569' }}>
                        {r.remarks || '—'}
                      </TableCell>
                      <TableCell align='right'>
                        <Tooltip title='Delete'>
                          <IconButton
                            size='small'
                            onClick={() => remove(r)}
                            sx={{ color: '#dc2626' }}>
                            <DeleteOutlineIcon sx={{ fontSize: 18 }} />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth='sm' fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>New Overtime Entry</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2} sx={{ pt: 1 }}>
            <Grid item xs={12}>
              <TextField
                select
                size='small'
                fullWidth
                label='Employee *'
                value={form.employeeId}
                onChange={(e) => setForm({ ...form, employeeId: e.target.value })}>
                {employees.map((e) => (
                  <MenuItem key={e.id} value={e.id}>
                    {e.employeeName} ({e.employeeCode})
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                type='date'
                size='small'
                fullWidth
                label='Date *'
                InputLabelProps={{ shrink: true }}
                value={form.otDate}
                onChange={(e) => setForm({ ...form, otDate: e.target.value })}
              />
            </Grid>
            <Grid item xs={6} sm={4}>
              <TextField
                type='number'
                size='small'
                fullWidth
                label='Hours *'
                inputProps={{ min: 0, step: 0.25 }}
                value={form.otHours}
                onChange={(e) => setForm({ ...form, otHours: e.target.value })}
              />
            </Grid>
            <Grid item xs={6} sm={4}>
              <TextField
                type='number'
                size='small'
                fullWidth
                label='Rate / hr *'
                inputProps={{ min: 0 }}
                value={form.otRate}
                onChange={(e) => setForm({ ...form, otRate: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                size='small'
                fullWidth
                multiline
                rows={2}
                label='Remarks'
                value={form.remarks}
                onChange={(e) => setForm({ ...form, remarks: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <Typography sx={{ color: '#64748B' }}>
                Amount preview: ₹
                {(
                  Number(form.otHours || 0) * Number(form.otRate || 0)
                ).toLocaleString('en-IN')}
              </Typography>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setDialogOpen(false)} disabled={saving}>
            Cancel
          </Button>
          <Button
            variant='contained'
            onClick={save}
            disabled={saving}
            sx={{ bgcolor: '#f97316', '&:hover': { bgcolor: '#ea580c' } }}>
            {saving ? 'Saving…' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snack.open}
        autoHideDuration={3500}
        onClose={() => setSnack({ ...snack, open: false })}>
        <Alert severity={snack.sev} variant='filled'>
          {snack.msg}
        </Alert>
      </Snackbar>
    </Box>
  );
}
